import React from 'react';
import { Phone, Calendar, FileText, UserCheck, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal from './ScrollReveal';

const GettingStartedSection = ({ onBookAppointment }) => {
  const steps = [
    {
      icon: <Phone className="w-8 h-8" />,
      title: 'Contact Us',
      description: 'Reach out via phone, email, or our online booking system. We\'ll respond within 24 hours.',
      action: 'Call (877) 772-5150',
      href: 'tel:+18777725150',
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Schedule Initial Appointment',
      description: 'Choose a convenient time for your first visit. We offer flexible scheduling including evening appointments.',
      action: 'Book Online',
      onClick: onBookAppointment,
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Complete Intake Forms',
      description: 'Complete your intake and consent forms online before your visit, or download a printable packet.',
      action: 'Complete Intake Online',
      href: '/intake',
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: 'Meet Your Provider',
      description: 'Comprehensive assessment with your clinician to understand your needs and goals.',
      action: 'View Our Team',
      href: '/team',
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Begin Your Journey',
      description: 'Start your personalized treatment plan with ongoing support from our caring team.',
      action: 'Get Started',
      href: '/get-started',
    }
  ];

  return (
    <section id="getting-started" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50" data-testid="getting-started-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Taking the first step is the hardest part. We've made it simple and straightforward.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line - Desktop */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>

          {/* Steps */}
          <div className="space-y-12">
            {steps.map((step, index) => (
              <ScrollReveal
                key={step.title}
                direction={index % 2 === 0 ? 'right' : 'left'}
                delay={index * 0.1}
              >
              <div
                className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                data-testid={`step-${index + 1}`}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className={`flex items-center gap-4 mb-4 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                      <div className="text-blue-600">{step.icon}</div>
                      <h3 className="text-xl font-bold text-gray-900">Step {index + 1}: {step.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    {step.href ? (
                      step.href.startsWith('tel:') || step.href.startsWith('http') ? (
                        <a href={step.href} className="text-blue-600 font-medium hover:text-blue-700">
                          {step.action} →
                        </a>
                      ) : (
                        <Link to={step.href} className="text-blue-600 font-medium hover:text-blue-700">
                          {step.action} →
                        </Link>
                      )
                    ) : step.onClick ? (
                      <button onClick={step.onClick} className="text-blue-600 font-medium hover:text-blue-700">
                        {step.action} →
                      </button>
                    ) : (
                      <span className="text-blue-600 font-medium">{step.action} →</span>
                    )}
                  </div>
                </div>

                {/* Circle Marker */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {index + 1}
                  </div>
                </div>

                {/* Spacer for alignment */}
                <div className="flex-1 hidden md:block"></div>
              </div>
              </ScrollReveal>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <button
            onClick={onBookAppointment}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl text-lg font-semibold"
            data-testid="getting-started-book-btn"
          >
            Start Your Journey Today
          </button>
        </div>
      </div>
    </section>
  );
};

export default GettingStartedSection;